<template>
    <div class="step-source-analysis">
        <h4 v-if="!analyzing && !isLoading" class="step-title">IVR Source</h4>

        <div class="analysis-container">
            <!-- AI-Style Loading -->
            <div v-if="analyzing || isLoading" class="ai-loading-state">
                <div class="ai-loading-content">
                    <div class="ai-pulse-container">
                        <div class="ai-pulse-ring"></div>
                        <div class="ai-pulse-ring"></div>
                        <div class="ai-pulse-ring"></div>
                        <i class="el-icon-cpu ai-cpu-icon"></i>
                    </div>
                    <h4 class="ai-loading-title">{{ loadingText }}</h4>
                    <p class="ai-loading-subtitle">Analyzing your IVR and preparing the conversion.</p>
                    <div class="loading-details">
                        <div class="detail-item">
                            <i class="el-icon-loading mr-2"></i>
                            Analyzing IVR configuration
                        </div>
                        <div class="detail-item">
                            <i class="el-icon-loading mr-2"></i>
                            Transcribing audio prompts
                        </div>
                        <div class="detail-item">
                            <i class="el-icon-loading mr-2"></i>
                            Detecting language
                        </div>
                    </div>
                </div>
            </div>

            <!-- Content after loading -->
            <div v-else-if="analysis">
                <p class="step-description">
                    Review the analyzed IVR configuration below.
                </p>
                
                <!-- IVR Info -->
                <div class="info-card mb-3">
                <h5>IVR Information</h5>
                <div class="info-row">
                    <label>Name:</label>
                    <span>{{ analysis.ivr.name }}</span>
                </div>
                <div class="info-row">
                    <label>Source Type:</label>
                    <el-tag 
                        :class="['prompt-type-tag', analysis.prompt_type === 'audio' ? 'prompt-type-audio' : 'prompt-type-tts']" 
                        size="small">
                        <i :class="analysis.prompt_type === 'audio' ? 'fas fa-headphones' : 'fas fa-microphone'"></i>
                        <span class="prompt-type-text">{{ analysis.prompt_type === 'audio' ? 'Audio File' : 'Text to Speech' }}</span>
                    </el-tag>
                </div>
                <div
                    v-if="analysis.prompt_type === 'audio' && analysis.original_recording_url"
                    class="info-row">
                    <label>Original Recording:</label>
                    <audio
                        ref="originalAudio"
                        controls
                        :src="analysis.original_recording_url"
                        @error="handleAudioError"
                        class="original-audio-player">
                        Your browser does not support the audio element.
                    </audio>
                </div>
            </div>

            <!-- Language Selection -->
            <div class="form-section">
                <h5>Language</h5>
                <el-select
                    v-model="selectedLanguage"
                    filterable
                    placeholder="Select language"
                    class="w-100"
                    :key="`language-select-${isVoiceDataReady}`"
                    @change="handleLanguageChange"
                    v-if="isVoiceDataReady && filteredLanguages && filteredLanguages.length > 0">
                    <el-option
                        v-for="lang in filteredLanguages"
                        :key="lang.code"
                        :label="lang.name"
                        :value="lang.code">
                    </el-option>
                </el-select>
                <div v-else class="text-muted">Loading languages...</div>
                <small v-if="analysis.transcription && analysis.language" class="text-muted">
                    Detected: {{ getLanguageName(analysis.language) }}
                </small>
            </div>

            <!-- Greeting -->
            <div class="form-section">
                <h5>
                    IVA Greeting <span class="text-danger">*</span>
                    <el-tag v-if="analysis.transcription" 
                            :type="getConfidenceType(analysis.transcription.confidence)" 
                            size="mini"
                            class="ml-2">
                        {{ Math.round(analysis.transcription.confidence * 100) }}% confidence
                    </el-tag>
                </h5>
                <small>This is what the IVA will say to answer the call.</small>
                <el-input
                    type="textarea"
                    :rows="3"
                    v-model="greeting"
                    ref="greetingInput"
                    placeholder="Enter the greeting your IVA will use..."
                    maxlength="1000"
                    show-word-limit
                    @focus="onGreetingFocus"
                    @blur="onGreetingBlur"
                    class="mt-2">
                </el-input>
                <el-tooltip content="Add variable" placement="top">
                    <variables @variable-selected="variableSelectedOnGreeting"/>
                </el-tooltip>
            </div>

            <!-- Voice Selection -->
            <div class="form-section">
                <h5>Select Voice <span class="text-danger">*</span></h5>
                <small>Choose a preset voice for this agent.</small>
                <div class="d-flex align-items-center voice-selector-width mt-2">
                    <el-select
                        v-model="selectedVoiceId"
                        filterable
                        placeholder="Select a voice"
                        class="w-100"
                        @change="handleVoiceChange"
                        v-if="isVoiceDataReady && groupedVoices && groupedVoices.length > 0">
                        <el-option-group
                            v-for="group in groupedVoices"
                            :key="group.label"
                            :label="group.label">
                            <el-option
                                v-for="voice in group.voices"
                                :key="voice.id"
                                :label="getFormattedVoiceLabel(voice)"
                                :value="voice.id">
                                <template #default>
                                    <div class="voice-option">
                                        <span class="gender-circle"
                                              :class="getGenderClass(voice.gender)">
                                            {{ getGenderName(voice.gender) }}
                                        </span>
                                        {{ getVoiceName(voice) }}
                                    </div>
                                    <div class="voice-tags mb-1 w-75">
                                        <el-tag
                                            v-if="getLanguage(voice.language)"
                                            size="mini"
                                            class="mr-1 voice-tag"
                                            type="info">
                                            {{ getLanguage(voice.language) }}
                                            <span
                                                v-if="getAccent(voice.accent)"
                                                class="accent-tag">
                                                ({{ getAccent(voice.accent) }})
                                            </span>
                                        </el-tag>
                                    </div>
                                </template>
                            </el-option>
                        </el-option-group>
                    </el-select>
                    <div v-else class="text-muted">Loading voices...</div>
                    <el-button
                        v-if="selectedVoiceId"
                        class="ml-2"
                        circle
                        @click="toggleAudio(selectedVoiceId)">
                        <i :class="isPlaying ? 'fas fa-stop' : 'fas fa-play'"></i>
                    </el-button>
                </div>
            </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import { mapActions, mapGetters, mapState } from 'vuex'
import * as AloAi from '../../constants/aloai'
import audioPlayerMixin from '../../mixins/audio-player-mixin'
import voiceUtilsMixin from '../../mixins/voice-utils-mixin'
import Variables from '../messenger/variables.vue'

export default {
    name: 'Step1SourceAnalysis',

    mixins: [audioPlayerMixin, voiceUtilsMixin],

    components: {
        Variables
    },

    props: {
        ivrId: {
            type: Number,
            required: true
        },
        wizardData: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            AloAi,
            analyzing: false,
            analysis: null,
            promptText: '',
            selectedLanguage: '',
            greeting: '',
            selectedVoiceId: null,
            selectedVoiceModel: null,
            greetingCursorPosition: 0,
            loadingText: 'We\'re analyzing your IVR...'
        }
    },

    computed: {
        ...mapState('aloai', ['voices', 'languages']),
        ...mapGetters('aloai', [
            'getVoicesLoading',
            'getLanguagesLoading',
            'getFilteredLanguages',
            'getDefaultVoiceForModel'
        ]),

        isLoading() {
            return this.getLanguagesLoading || this.getVoicesLoading
        },

        filteredLanguages() {
            // If no voice selected yet, return all languages
            if (!this.selectedVoiceId) {
                return this.languages || []
            }
            return this.getFilteredLanguages(this.selectedVoiceId) || []
        },

        voices_list() {
            return this.voices || []
        },

        // Group voices by provider for display
        groupedVoices() {
            if (!this.voices || this.voices.length === 0) {
                return []
            }

            const groups = {}
            this.voices.forEach(voice => {
                const provider = voice.provider || 'Other'
                if (!groups[provider]) {
                    groups[provider] = {
                        label: provider.charAt(0).toUpperCase() + provider.slice(1),
                        voices: []
                    }
                }
                groups[provider].voices.push(voice)
            })

            return Object.values(groups)
        },

        isVoiceDataReady() {
            return this.voices && this.voices.length > 0 &&
                   this.languages && this.languages.length > 0
        }
    },

    async mounted() {
        // Load voice data
        await this.fetchAllVoiceData()
        
        // Then analyze IVR
        await this.analyzeIvr()
    },

    methods: {
        ...mapActions('aloai', ['fetchVoices', 'fetchVoiceModels', 'fetchLanguages', 'fetchAllVoiceData']),

        async analyzeIvr() {
            this.analyzing = true

            try {
                const response = await axios.post(`/api/v1/iva-wizard/analyze-ivr/${this.ivrId}`)

                if (response.data.success) {
                    this.analysis = response.data.data
                    this.promptText = this.analysis.prompt_text || ''
                    
                    // Pre-populate greeting with AI-generated suggestion (prioritize it over raw transcription)
                    this.greeting = this.analysis.greeting_suggestion || this.analysis.prompt_text || ''
                    
                    // Set defaults with proper timing
                    await this.setDefaultValues()
                } else {
                    this.$message.error(response.data.message || 'Analysis failed')
                }
            } catch (error) {
                console.error('IVR Analysis Error:', error)
                this.$message.error('Analysis failed: ' + (error.response?.data?.message || error.message))
            } finally {
                this.analyzing = false
            }
        },

        async setDefaultValues() {
            // Wait for voice data to be fully loaded
            await this.$nextTick()
            
            // Set default voice model to match normal bot creation - GPT-4.1 Mini (VOICE_MODEL_GPT_41_MINI)
            // This is the same default as dialog.vue line 290
            if (!this.selectedVoiceModel) {
                this.selectedVoiceModel = AloAi.VOICE_MODEL_GPT_41_MINI
            }
            
            // Set default voice if none selected - use the default voice getter to select Adrian
            if (!this.selectedVoiceId && this.voices && this.voices.length > 0) {
                const defaultVoice = this.getDefaultVoiceForModel(this.selectedVoiceModel)
                this.selectedVoiceId = defaultVoice ? defaultVoice.id : this.voices[0].id
            }
            
            // Wait for select components to be rendered with voice data
            await this.$nextTick()
            
            // Set language AFTER voice data is ready - use detected language or default to English
            if (!this.selectedLanguage && this.filteredLanguages && this.filteredLanguages.length > 0) {
                const languageCode = this.analysis.language || 'en'
                // Verify the language exists in our data
                const languageExists = this.filteredLanguages.find(l => l.code === languageCode)
                if (languageExists) {
                    this.selectedLanguage = languageCode
                } else {
                    // Fallback to first available language
                    this.selectedLanguage = this.filteredLanguages[0].code
                }
            }
            
            // Wait one more tick to ensure language select is rendered
            await this.$nextTick()
            
            // Force update to ensure UI reflects the changes
            this.$forceUpdate()
            
            // Emit the update
            this.emitUpdate()
        },


        handleLanguageChange() {
            this.emitUpdate()
        },

        onGreetingFocus(event) {
            this.greetingCursorPosition = event.target.selectionStart
        },

        onGreetingBlur(event) {
            this.greetingCursorPosition = event.target.selectionStart
        },

        variableSelectedOnGreeting(variable) {
            let newContent = this.greeting
            newContent = newContent.slice(0, this.greetingCursorPosition) + variable + newContent.slice(this.greetingCursorPosition)
            this.greetingCursorPosition = this.greetingCursorPosition + variable.length
            this.greeting = newContent
            
            // Focus back on the textarea
            this.$nextTick(() => {
                const textarea = this.$refs.greetingInput?.$el?.querySelector('textarea')
                if (textarea) {
                    textarea.focus()
                    textarea.setSelectionRange(this.greetingCursorPosition, this.greetingCursorPosition)
                }
            })
            
            this.emitUpdate()
        },

        handleVoiceChange(voiceId) {
            this.stopAudio()

            // Store the previous voice ID for potential rollback
            const previousVoiceId = this.selectedVoiceId

            // Check if current model is S2S and if selected voice is compatible
            const isS2sModel = this.selectedVoiceModel === AloAi.VOICE_MODEL_GPT_REALTIME_15 ||
                               this.selectedVoiceModel === AloAi.VOICE_MODEL_GPT_REALTIME_MINI

            if (isS2sModel) {
                // Get selected voice
                const selectedVoice = this.voices_list.find(voice => voice.id === voiceId)

                // Check if voice is compatible (must be OpenAI and not in unsupported list)
                const isVoiceCompatible = selectedVoice &&
                                          selectedVoice.provider === AloAi.PROVIDER_OPENAI &&
                                          !AloAi.UNSUPPORTED_S2S_VOICES.includes(selectedVoice.external_id)

                // If not compatible, show error and reset to previous voice
                if (!isVoiceCompatible) {
                    this.$message.error('The selected voice is not compatible with the current voice model. Please choose an OpenAI compatible voice.')
                    // Reset voice selection to previous voice
                    this.selectedVoiceId = previousVoiceId
                    return
                }
            }

            this.selectedVoiceId = voiceId
            this.emitUpdate()
        },



        emitUpdate() {
            this.$emit('update', {
                analysisData: this.analysis,
                greeting: this.greeting,
                prompt_text: this.promptText, // Pass original transcription for instruction generation
                language: this.selectedLanguage,
                voice_id: this.selectedVoiceId, // Send database ID (same as normal bot creation)
                voice_model: this.selectedVoiceModel,
            })
        },

        getConfidenceType(confidence) {
            if (confidence >= 0.9) return 'success'
            if (confidence >= 0.75) return 'warning'
            return 'danger'
        },

        getLanguageName(code) {
            const languages = {
                en: 'English',
                'en-US': 'English',
                es: 'Spanish',
                'es-419': 'Spanish',
                fr: 'French',
                de: 'German',
                it: 'Italian',
                pt: 'Portuguese',
                nl: 'Dutch',
                zh: 'Chinese',
                ja: 'Japanese',
                ko: 'Korean',
                multi: 'Multilingual (English and Spanish)'
            }
            return languages[code] || code
        },

        handleAudioError(event) {
            console.error('Audio loading error:', event)
            this.$message.error('Failed to load audio recording')
        },

        validate() {
            if (!this.greeting) {
                this.$message.warning('Please enter a greeting for your IVA')
                return false
            }
            if (!this.selectedVoiceId) {
                this.$message.warning('Please select a voice')
                return false
            }
            // Voice model is set automatically to default, no need to validate
            return true
        },

        stopAllAudio() {
            // Stop voice preview audio (from mixin)
            this.stopAudio()

            // Stop original recording audio
            if (this.$refs.originalAudio) {
                this.$refs.originalAudio.pause()
                this.$refs.originalAudio.currentTime = 0
            }
        }
    },

    watch: {
        greeting() {
            this.emitUpdate()
        },

        isVoiceDataReady(newVal) {
            if (newVal) {
                // When voice data becomes ready, ensure defaults are set if analysis already loaded
                if (this.analysis && (!this.selectedVoiceId || !this.selectedVoiceModel || !this.selectedLanguage)) {
                    this.setDefaultValues()
                } else if (this.selectedVoiceId && this.selectedVoiceModel && this.selectedLanguage) {
                    // Force a re-render when voice data becomes available
                    this.$nextTick(() => {
                        this.$forceUpdate()
                    })
                }
            }
        }
    },

    beforeDestroy() {
        // Clean up audio when component is destroyed
        this.stopAudio()
    }
}
</script>

<style scoped>
.step-source-analysis {
    padding: 10px 0 0 0;
    padding-bottom: 0px;
}

.step-title {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 8px;
}

.step-description {
    font-size: 15px;
    color: #909399;
    margin: 0 0 15px 0;
    word-break: keep-all;
    word-wrap: break-word;
    overflow-wrap: break-word;
}

.info-card {
    background: #f8f9fa;
    padding: 15px;
    border-radius: 4px;
    border: 1px solid #e9ecef;
}

.info-card h5 {
    margin-top: 0;
    margin-bottom: 12px;
    font-size: 14px;
    font-weight: 600;
}

.info-row {
    display: flex;
    align-items: flex-start;
    margin-bottom: 8px;
    gap: 8px;
}

.info-row label {
    font-weight: 500;
    min-width: 150px;
    color: #666;
    flex-shrink: 0;
}

.info-row span {
    color: #333;
    word-wrap: break-word;
    overflow-wrap: break-word;
    word-break: keep-all;
    line-height: 1.5;
}

.info-row .el-tag:not(.prompt-type-tag) {
    display: inline-block;
    max-width: 100%;
    white-space: normal !important;
    word-wrap: break-word;
    overflow-wrap: break-word;
    word-break: keep-all;
    line-height: 1.5;
    padding: 4px 8px !important;
    height: auto !important;
}

.original-audio-player {
    flex: 1;
    max-width: 350px;
    height: 32px;
}

.form-section {
    margin-bottom: 20px;
}

.form-section h5 {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 10px;
}

.form-section small {
    display: block;
    margin-top: 5px;
    word-break: keep-all;
    overflow-wrap: break-word;
    word-wrap: break-word;
    line-height: 1.5;
}

.text-danger {
    color: #f56c6c;
}

.text-muted {
    color: #909399;
}

/* Voice selector styles */
.voice-selector-width {
    width: 100%;
    max-width: 500px;
}

.voice-option {
    display: flex;
    align-items: center;
}

.gender-circle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    margin-right: 5px;
    color: white;
    font-size: 10px;
}

.gender-male {
    background-color: rgb(86, 175, 248);
}

.gender-female {
    background-color: rgb(250, 202, 209);
    border: 1px solid pink;
    color: white;
}

.gender-unknown {
    background-color: grey;
}

.voice-tags {
    display: flex;
    flex-wrap: wrap;
}

.voice-tag {
    color: black;
}

.accent-tag {
    margin-left: 1px;
}

/* Model selector styles */
.unified-model-option {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    flex-wrap: wrap;
}

/* AI-Style Loading */
.ai-loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    padding: 60px 20px;
}

.ai-loading-content {
    text-align: center;
    max-width: 400px;
}

.ai-pulse-container {
    position: relative;
    width: 120px;
    height: 120px;
    margin: 0 auto 30px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.ai-cpu-icon {
    font-size: 48px;
    color: #409eff;
    z-index: 10;
    position: relative;
}

.ai-pulse-ring {
    position: absolute;
    width: 100%;
    height: 100%;
    border: 3px solid #409eff;
    border-radius: 50%;
    opacity: 0;
    animation: ai-pulse 2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

.ai-pulse-ring:nth-child(1) {
    animation-delay: 0s;
}

.ai-pulse-ring:nth-child(2) {
    animation-delay: 0.5s;
}

.ai-pulse-ring:nth-child(3) {
    animation-delay: 1s;
}

@keyframes ai-pulse {
    0% {
        transform: scale(0.5);
        opacity: 0;
    }
    50% {
        opacity: 0.4;
    }
    100% {
        transform: scale(1.3);
        opacity: 0;
    }
}

.ai-loading-title {
    font-size: 20px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 12px;
    background: linear-gradient(90deg, #409eff 0%, #67c23a 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.ai-loading-subtitle {
    font-size: 15px;
    color: #909399;
    margin: 0 0 8px 0;
    word-break: keep-all;
    word-wrap: break-word;
    overflow-wrap: break-word;
}

.loading-details {
    max-width: 400px;
    margin: 5px auto 0;
}

.detail-item {
    text-align: left;
    padding: 6px 12px;
    margin: 3px 0;
    background: #f5f7fa;
    border-radius: 4px;
    font-size: 13px;
    color: #606266;
    display: flex;
    align-items: center;
}

.detail-item i {
    color: #409eff;
}

/* Prompt Type Tags - Production Ready */
.prompt-type-tag {
    font-weight: 600;
    padding: 6px 14px;
    border-radius: 6px;
    display: inline-flex;
    max-width: 100%;
    align-items: center;
    gap: 8px;
    transition: all 0.2s ease;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.prompt-type-tag i {
    font-size: 14px;
}

.prompt-type-text {
    font-size: 13px;
    letter-spacing: 0.3px;
    white-space: normal;
    word-wrap: break-word;
    overflow-wrap: break-word;
    word-break: keep-all;
}

/* Audio File - Light Teal/Emerald Theme */
.prompt-type-audio {
    background: linear-gradient(135deg, #d0f4f1 0%, #e8fef0 100%);
    border: 1px solid #80e0d3;
    color: #0d7c6e;
}

.prompt-type-audio i {
    color: #0d7c6e;
}

.prompt-type-audio:hover {
    background: linear-gradient(135deg, #c0f0ec 0%, #d8fee8 100%);
    box-shadow: 0 2px 6px rgba(13, 124, 110, 0.15);
    transform: translateY(-1px);
}

/* Text-to-Speech - Light Blue Theme */
.prompt-type-tts {
    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
    border: 1px solid #90caf9;
    color: #0d47a1;
}

.prompt-type-tts i {
    color: #0d47a1;
}

.prompt-type-tts:hover {
    background: linear-gradient(135deg, #d3e8fc 0%, #aacefb 100%);
    box-shadow: 0 2px 6px rgba(13, 71, 161, 0.15);
    transform: translateY(-1px);
}

/* Fix word breaking in greeting textarea */
.el-textarea >>> .el-textarea__inner {
    white-space: pre-wrap;
    word-break: keep-all;
    overflow-wrap: break-word;
}

/* Fix long text in select options */
.el-select-dropdown__item {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.voice-option {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

</style>
